import { activeRecording, TEMPO_PRESETS } from './constants'
import type { TempoPercent, VideoTier } from './constants'

/**
 * Bumped whenever the contents of `public/media` change. It is appended to
 * every built-in media URL so the files can be served `immutable` for a year
 * without ever serving a stale mix. Uploaded recordings do not need it: their
 * bucket keys are unique per recording id.
 */
export const MEDIA_VERSION = '3'

/**
 * Video is served from R2 rather than bundled with the app, because Cloudflare
 * Pages rejects any single file over 25 MiB and the mosaic is 31 MB even after
 * re-encoding. Audio for the built-in recording and the poster are small
 * enough to ship with the build; uploaded recordings live entirely in R2.
 *
 * The R2 bucket must send `Access-Control-Allow-Origin` for the deployed
 * origin: the frame scheduler reads the MP4 with `fetch()` to demux it, which
 * is a stricter requirement than the old `<video src>` tag had.
 */
export const VIDEO_ORIGIN = (
  import.meta.env.VITE_VIDEO_ORIGIN ?? 'https://pub-d7f1531783244d28ad8c3556005b4087.r2.dev'
).replace(/\/$/, '')

export function stemUrls(tempo: TempoPercent): string[] {
  const bank = activeRecording().audio[tempo]
  if (!bank) throw new Error(`No ${tempo}% audio bank for this recording.`)
  return bank.urls
}

export type { VideoTier }

/**
 * Video tiers in preference order for this display. The full-resolution list
 * exists so the focused tile can be shown at 1:1 on a high-density display;
 * below that the half-size variant (when the recording has one) saves ~100 MB
 * of detail nobody can see.
 */
export function selectVideoTiers(): VideoTier[] {
  const rec = activeRecording()
  if (rec.videoHalf.length === 0) return rec.videoFull
  const shortest = Math.min(window.screen.width, window.screen.height)
  const dpr = window.devicePixelRatio || 1
  // The focused tile is rendered at roughly 40 % of the shorter viewport
  // dimension; anything under ~480 device pixels is served by the half-size
  // mosaic, whose tiles are 480x480.
  return shortest * dpr >= 1100 ? rec.videoFull : rec.videoHalf
}

export function posterUrl(): string | null {
  return activeRecording().posterUrl
}

export function isTempoPreset(value: number): value is TempoPercent {
  return TEMPO_PRESETS.includes(value)
}
